import { EmailHistory } from '../email/EmailHistory';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CurrencyExchangeRounded from '@mui/icons-material/CurrencyExchangeRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Money } from '../../components/Money';
import { StatusChip } from '../../components/StatusChip';
import { businessDate, dateTime, money, qty as fmtQty } from '../../lib/format';
import { noteDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { percent } from '../payments/allocation';
import { AllocationsTable } from '../payments/kit/AllocationsTable';
import { idempotency, invalidateMoney } from '../payments/kit/hooks';
import { links } from '../payments/kit/labels';
import { ReallocateDialog } from '../payments/kit/ReallocateDialog';
import { ReasonDialog } from '../payments/kit/ReasonDialog';
import {
  AllocationBar,
  BackLink,
  DetailSkeleton,
  ErrorState,
  InfoGrid,
  SectionCard,
  StatCard,
  StatGrid,
} from '../payments/kit/ui';
import { RefundDialog } from '../payments/RefundDialog';
import { useNote, type NoteKind } from './api';
import { MoreMenu } from '../../components/MoreMenu';
import { linkedAllocations, linkedKnown, useLinkedOne } from '../../components/useLinked';

const TEXT = {
  credit: {
    name: 'Credit note',
    party: 'customer' as const,
    ledger: 'RECEIVABLE' as const,
    source: 'Invoice',
    back: '/returns?tab=credit-notes',
    perm: 'sales.return',
  },
  debit: {
    name: 'Debit note',
    party: 'supplier' as const,
    ledger: 'PAYABLE' as const,
    source: 'Bill',
    back: '/returns?tab=debit-notes',
    perm: 'purchases.return',
  },
};

/** A credit note (customer) or debit note (supplier): what it credited, where it was applied. */
export function NoteDetailPage({ kind }: { kind: NoteKind }) {
  const t = TEXT[kind];
  const { id = '' } = useParams();
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const qc = useQueryClient();
  const note = useNote(kind, id);
  const linkedReturn = useLinkedOne(
    kind === 'credit' ? 'salesReturn' : 'purchaseReturn',
    note.data?.returnId,
  );
  const [dialog, setDialog] = useState<'cancel' | 'reallocate' | 'refund' | null>(null);

  const cancel = useMutation({
    mutationFn: async (reason: string): Promise<{ number: string }> => {
      const body = { reason, version: note.data?.version ?? 1 };
      const params = { path: { id }, ...idempotency() };
      return kind === 'credit'
        ? unwrap(api.POST('/credit-notes/{id}/cancel', { params, body }))
        : unwrap(api.POST('/debit-notes/{id}/cancel', { params, body }));
    },
    onSuccess: async (n) => {
      notify.success(`${n.number} cancelled`);
      await invalidateMoney(qc);
    },
    onError: (e) => notify.error(e),
  });

  if (note.isLoading) return <DetailSkeleton />;
  if (note.isError || !note.data)
    return (
      <>
        <BackLink to={t.back} label="Returns & notes" />
        <ErrorState error={note.error} onRetry={() => void note.refetch()} />
      </>
    );

  const n = note.data;
  const posted = n.status === 'POSTED';
  const fromReturn = Boolean(n.returnId);
  const hasCredit = posted && Number(n.unallocated) > 0;
  const refunded = n.allocations.some((a) => !a.reversed && a.target.type === 'REFUND');
  const sourceLink =
    kind === 'credit' ? links.salesInvoice(n.source.id) : links.supplierInvoice(n.source.id);
  const returnLink = n.returnId
    ? kind === 'credit'
      ? links.salesReturn(n.returnId)
      : links.purchaseReturn(n.returnId)
    : null;

  return (
    <>
      <BackLink to={t.back} label="Returns & notes" />
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { md: 'flex-end' } }}
      >
        <Box>
          <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h3">{n.number}</Typography>
            <StatusChip status={n.status} />
            <Chip
              size="small"
              variant="outlined"
              label={fromReturn ? `${t.name} · from a return` : `${t.name} · adjustment`}
            />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            <Link
              component={RouterLink}
              to={links.contact(n.contact.id, t.party)}
              underline="hover"
              sx={{ fontWeight: 600 }}
            >
              {n.contact.name}
            </Link>{' '}
            · on{' '}
            <Link component={RouterLink} to={sourceLink} underline="hover" sx={{ fontWeight: 600 }}>
              {n.source.number}
            </Link>
            {n.source.extra ? ` (${n.source.extra})` : ''} · {businessDate(n.docDate, format)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintRounded />}
            onClick={() => void navigate(`/returns/${kind}-notes/${n.id}/print`)}
          >
            Print
          </Button>
          <WhatsappA4
            print={false}
            type={kind === 'credit' ? 'credit_note' : 'debit_note'}
            id={n.id}
            number={n.number}
            contact={n.contact}
            vars={{ amount: money(n.total, format), date: businessDate(n.docDate, format) }}
            doc={() => noteDoc(n, format)}
          />
          {posted && can('payments.allocate') && (
            <Button
              variant="outlined"
              startIcon={<SwapHorizRounded />}
              onClick={() => setDialog('reallocate')}
            >
              Reallocate
            </Button>
          )}
          {hasCredit && can('payments.create', 'payments.refund') && (
            <Button
              variant="outlined"
              startIcon={<CurrencyExchangeRounded />}
              onClick={() => setDialog('refund')}
            >
              {kind === 'credit' ? 'Refund customer' : 'Record supplier refund'}
            </Button>
          )}
          {posted && !fromReturn && can(t.perm) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockRounded />}
              disabled={refunded}
              title={refunded ? 'Part of it was refunded — void the refund first' : undefined}
              onClick={() => setDialog('cancel')}
            >
              Cancel note
            </Button>
          )}
          <MoreMenu
            linked={[
              kind === 'credit'
                ? linkedKnown('invoices', [n.source], 'Invoice')
                : linkedKnown('bills', [n.source], 'Bill'),
              linkedReturn,
              ...linkedAllocations(n.allocations.filter((a) => a.target.id !== n.source.id)),
            ]}
            exportModel={kind === 'credit' ? 'credit_note' : 'debit_note'}
            exportId={n.id}
          />
        </Stack>
      </Stack>

      {!posted && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
          Cancelled {dateTime(n.cancelledAt, format)}
          {n.cancelReason ? ` — “${n.cancelReason}”` : ''}. Its entry and allocations were reversed.
        </Alert>
      )}
      {posted && fromReturn && returnLink && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5 }}>
          Issued by{' '}
          <Link component={RouterLink} to={returnLink} sx={{ fontWeight: 650 }}>
            a return
          </Link>
          . Returns are final, so this note can’t be cancelled — but its credit can be reallocated
          or refunded.
        </Alert>
      )}

      <StatGrid>
        <StatCard
          label="Total"
          value={<Money value={n.total} />}
          hint={`Net ${money(n.netTotal, format)} · tax ${money(n.taxTotal, format)}`}
          icon={<ReceiptRounded />}
          tone="primary"
        />
        <StatCard
          label="Applied"
          value={<Money value={n.allocated} />}
          hint={
            <Box sx={{ mt: 0.75 }}>
              <AllocationBar value={percent(n.allocated, n.total)} />
            </Box>
          }
          icon={<CheckCircleRounded />}
          tone="success"
        />
        <StatCard
          label="Credit left"
          value={<Money value={n.unallocated} />}
          hint={
            Number(n.unallocated) > 0
              ? kind === 'credit'
                ? 'Customer credit — refund or use later'
                : 'Supplier owes you this'
              : 'Fully used'
          }
          icon={<SavingsRounded />}
          tone={Number(n.unallocated) > 0 ? 'warning' : 'neutral'}
        />
        <StatCard
          label={t.source}
          value={n.source.number}
          hint={n.source.extra ?? (kind === 'credit' ? 'Sales invoice' : 'Supplier bill')}
          icon={<AccountBalanceWalletRounded />}
          tone="info"
        />
      </StatGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1.6fr 1fr' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2.5} sx={{ minWidth: 0 }}>
          <SectionCard flush title="Lines" subtitle="What was credited on each line">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Line</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Net
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      Tax
                    </TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {n.lines.map((l) => (
                    <TableRow key={l.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {l.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {l.qtyBase !== null
                            ? `${fmtQty(l.qtyBase, format)} returned (base units)`
                            : 'Price adjustment'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Money value={l.netAmount} />
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Money value={l.taxAmount} />
                      </TableCell>
                      <TableCell align="right">
                        <Money value={l.lineTotal} strong />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
          <SectionCard
            flush
            title="Where it was applied"
            subtitle={`${t.source}s it settled, and refunds that paid it out`}
          >
            <AllocationsTable
              allocations={n.allocations}
              selfId={n.id}
              emptyText="Not applied yet — the full amount is credit."
            />
          </SectionCard>
        </Stack>
        <SectionCard title="Details">
          <InfoGrid
            items={[
              { label: kind === 'credit' ? 'Customer' : 'Supplier', value: n.contact.name },
              { label: t.source, value: n.source.number },
              { label: 'Date', value: businessDate(n.docDate, format) },
              { label: 'Reason', value: n.reason ?? '—' },
              { label: 'Posted', value: dateTime(n.postedAt, format) },
            ]}
          />
          {n.notes && (
            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '10px', bgcolor: 'action.hover' }}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {n.notes}
              </Typography>
            </Box>
          )}
        </SectionCard>
      </Box>

      <ReasonDialog
        open={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        title={`Cancel ${n.number}?`}
        confirmLabel="Cancel note"
        body={
          <>
            This posts an exact reversing entry for <b>{money(n.total, format)}</b> and undoes where
            it was applied, so {n.source.number} and any other documents it settled are open again.
            This can’t be undone.
          </>
        }
        onConfirm={(reason) => cancel.mutateAsync(reason)}
      />
      {posted && (
        <ReallocateDialog
          open={dialog === 'reallocate'}
          onClose={() => setDialog(null)}
          contactId={n.contact.id}
          ledger={t.ledger}
          credit={{
            type: kind === 'credit' ? 'CREDIT_NOTE' : 'DEBIT_NOTE',
            id: n.id,
            number: n.number,
          }}
          allocations={n.allocations}
          unallocated={n.unallocated}
        />
      )}
      {hasCredit && (
        <RefundDialog
          open={dialog === 'refund'}
          onClose={() => setDialog(null)}
          contact={n.contact}
          party={t.party}
        />
      )}
      <AttachmentsPanel
        recordType={kind === 'credit' ? 'CREDIT_NOTE' : 'DEBIT_NOTE'}
        recordId={n.id}
        sx={{ mt: 3 }}
      />
      <WhatsappHistory
        documentType={kind === 'credit' ? 'credit_note' : 'debit_note'}
        documentId={n.id}
        sx={{ mt: 3 }}
      />
      <EmailHistory
        documentType={kind === 'credit' ? 'credit_note' : 'debit_note'}
        documentId={n.id}
        sx={{ mt: 3 }}
      />
    </>
  );
}
